import { useCallback, useMemo, useRef, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError, UserFriendlyError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColetaPrecoForm, ColetaPrecoSchema, initialColetaPrecoForm } from "../schemas/ColetaPrecos_cadastro/coleta_preco";
import { StringUtils } from "../../../../common/utils/string_utils";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { RequisicaoCompraDropDown } from "../../../../common/domain/models/requisicao_compra_dropdown";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { SubcontaDropdown } from "../../../rh/domain/models/subconta_dropdown";
import { ColetaPreco } from "../../domain/models/coleta_preco";
import { AtributoCompra } from "../../../../common/domain/models/atributo_compra";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { RequisicaoProdutoServicoDropDown } from "../../domain/models/requisicao_produto_servico_dropdown";
import { ColetaPrecoFornecedorForm } from "../schemas/ColetaPrecos_cadastro/coleta_preco_fornecedor";
import { ColetaPrecoFornecedor } from "../../domain/models/coleta_preco_fornecedor";
import { ColetaProdutoServicoLista } from "../../domain/models/coleta_produto_servico_lista";
import { ColetaPrecoFornecedorEditForm } from "../schemas/ColetaPrecos_cadastro/coleta_preco_fornecedor_edit";
import { ConfigurarPagamentoForm } from "../schemas/ColetaPrecos_cadastro/configurar_pagamento";
import { ConfigurarPagamentoColeta } from "../../domain/models/configurar_pagamento_coleta";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { comprasItemTotal, roundComprasMoney } from "../utils/compras_valores";
import { useFornecedorDrawer } from "../../../../common/components/drawers_api/drawers/fornecedor_drawer";
import { FuncionarioAprovacaoDropDown } from "../../domain/models/funcionario_aprovacao";
import { useEmailModal } from "../../../../common/components/email_modal";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";

import dayjs from "dayjs";

/**
 * useColetaPrecosCadastroViewModel
 */

export const useColetaPrecosCadastroViewModel = () => {
  const {
    reports: {
      getReportColetaPrecoUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFuncionarioByIdUsuarioUseCase,
      getAtributosCompraUseCase,
    },
    compras: {
      getFornecedorByFamiliaDropDownUseCase,
      getProdutosServicosByIdRequisicaoCompraDropDownUseCase,
      getFornecedoresDetalheDropDownUseCase,
      getColetaPrecoByIdUseCase,
      finalizarRequisicaoCompraUseCase,
      createColetaPrecoUseCase,
      updateColetaPrecoUseCase,
      createColetaPrecoFornecedorUseCase,
      updateColetaPrecoFornecedorUseCase,
      deleteColetaPrecoFornecedorUseCase,
      copyColetaPrecoFornecedorUseCase,
      getAllColetaPrecoFornecedorUseCase,
      getAllColetaProdutoServicoUseCase,
      aprovarColetaPrecoFornecedorUseCase,
      cancelarAprovacaoColetaPrecoFornecedorUseCase,
      gerarOrdemCompraColetaPrecoUseCase,
      configurarPagamentoColetaPrecoUseCase,
      getConfigurarPagamentoColetaPrecoUseCase,
      deleteConfigurarPagamentoColetaPrecoUseCase,
      deleteColetaPrecoUseCase,
      deleteColetaProdutoServicoUseCase,
      createFornecedorUseCase,
      getAllFornecedorQualificacaoUseCase,
      downloadColetaPrecoArquivoUseCase,
      getFuncionariosAprovadoresDropDownUseCase,
      sendAprovacaoColetaPrecoUseCase,
    },
  } = useDependencyInjection();

  const {
    loadRequisicaoCompraDropDown,
    loadFuncionariosDropDown,
    loadCentroCustoDropDown,
    loadSubContasDropDown,
  } = useDropDownLoader();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
    setCustomLoading,
  } = usePageLoader();

  const showEmailAprovacaoModalRef = useRef(false);

  const { idUsuario } = useUserAuthStore();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [usuarioFuncionarioInfo, setUsuarioFuncionarioInfo] = useState<FuncionarioInformation>();

  const [idColetaPreco, setIdColetaPreco] = useState<number>();
  const [coletaPreco, setColetaPreco] = useState<ColetaPreco>();
  const [coletaPrecoFornecedores, setColetaPrecoFornecedores] = useState<ColetaPrecoFornecedor[]>();
  const [coletaProdutosServicos, setColetaProdutosServicos] = useState<ColetaProdutoServicoLista[]>();

  const [selectedColetaPrecoFornecedor, setSelectedColetaPrecoFornecedor] = useState<ColetaPrecoFornecedorEditForm>();
  const [idsColetaProdutoServico, setIdsColetaProdutoServico] = useState<number[]>([]);

  const [atributoCompra, setAtributoCompra] = useState<AtributoCompra>();

  const [configurarPagamentoColeta, setConfigurarPagamentoColeta] = useState<ConfigurarPagamentoColeta>();

  const [coletaPrecoFornecedorModal, setColetaPrecoFornecedorModal] = useState(false);
  const [configurarPagamentoModal, setConfigurarPagamentoModal] = useState(false);

  const [requisicoesComprasDropdown, setRequisicoesComprasDropdown] = useState<RequisicaoCompraDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>();
  const [subcontasDropdown, setSubcontasDropdown] = useState<SubcontaDropdown[]>();
  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [produtosServicosDropdown, setProdutosServicosDropdown] = useState<RequisicaoProdutoServicoDropDown[]>();
  const [funcionariosAprovacaoDropdown, setFuncionariosAprovacaoDropdown] = useState<FuncionarioAprovacaoDropDown[]>();

  const [coletaPrecoDeleteModal, setColetaPrecoDeleteModal] = useState(false);
  const [coletaPrecoFornecedorDeleteModal, setColetaPrecoFornecedorDeleteModal] = useState(false);
  const [coletaPrecoFornecedorCopyModal, setColetaPrecoFornecedorCopyModal] = useState(false);

  const [selectedColetaPrecoFornecedorIdx, setSelectedColetaPrecoFornecedorIdx] = useState<number>(1);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const [fornecedorStateDrawer, startFornecedorDrawer] = useFornecedorDrawer();

  const [
    emailState,
    setEmailOpenState,
    setEmailFields,
  ] = useEmailModal();

  const hookForm = useForm<ColetaPrecoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialColetaPrecoForm,
    resolver: zodResolver(ColetaPrecoSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  const [status] = hookForm.watch(['status']);

  const coletaPrecoFornecedorAprovada = useMemo(() => {
    if (coletaPrecoFornecedores === undefined || coletaPrecoFornecedores.length === 0) return null;
    return coletaPrecoFornecedores.find(x => x.aprovado === true) ?? null;
  }, [
    coletaPrecoFornecedores,
  ]);

  const subTotal = useMemo(() => {
    if (coletaProdutosServicos === undefined || coletaProdutosServicos.length === 0) {
      return null;
    }

    const result = coletaProdutosServicos.reduce((acc, value) => {
      if (value.quantidadeColeta === null || value.valorUnitario === null) {
        return acc;
      }
      return acc + comprasItemTotal(value.quantidadeColeta, value.valorUnitario);
    }, 0);

    return roundComprasMoney(result);
  }, [
    coletaProdutosServicos,
  ]);

  const total = useMemo(() => {
    if (subTotal === null) return null;
    if (selectedColetaPrecoFornecedor === undefined) return null;

    let value = subTotal;

    if (selectedColetaPrecoFornecedor.imposto !== null) {
      value += roundComprasMoney(selectedColetaPrecoFornecedor.imposto);
    }
    if (selectedColetaPrecoFornecedor.frete !== null) {
      value += roundComprasMoney(selectedColetaPrecoFornecedor.frete);
    }
    if (selectedColetaPrecoFornecedor.acrescimo !== null) {
      value += roundComprasMoney(selectedColetaPrecoFornecedor.acrescimo);
    }
    if (selectedColetaPrecoFornecedor.desconto !== null) {
      value -= roundComprasMoney(selectedColetaPrecoFornecedor.desconto);
    }

    return roundComprasMoney(value);
  }, [
    subTotal,
    selectedColetaPrecoFornecedor,
  ]);

  const funcionariosCompradorDropdown = useMemo(() => {
    return funcionariosDropdown
      ? funcionariosDropdown.filter(x => {
        if (coletaPreco) {
          if (x.idFuncionario === coletaPreco.idFuncionarioComprador) {
            return true;
          }
        }
        return x.canBeSelected === true;
      })
      : null;
  }, [
    coletaPreco,
    funcionariosDropdown,
  ]);

  const filteredFuncionariosAprovadorDropdown = useMemo(() => {
    if (funcionariosAprovacaoDropdown === undefined) return null;
    if (funcionariosAprovacaoDropdown.length === 0) return null;

    if (!funcionariosAprovacaoDropdown.some(x => x.canBeSelected)) {
      return funcionariosAprovacaoDropdown;
    }

    return funcionariosAprovacaoDropdown.filter(x => {
      if (coletaPreco && coletaPreco.funcionariosAprovadores) {
        if (coletaPreco.funcionariosAprovadores.some(y => y === x.idFuncionario)) {
          return true;
        }
      }
      return x.canBeSelected === true;
    });
  }, [
    coletaPreco,
    funcionariosAprovacaoDropdown,
  ]);

  const buttonOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Nova coleta',
      },
      {
        id: 2,
        visibility: idColetaPreco !== undefined && idColetaPreco !== null,
        label: 'Excluir',
      },
    ];
  }, [
    idColetaPreco,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Em Elaboração',
        disabled: coletaPreco !== undefined && (coletaPreco.status === 2 || coletaPreco.status === 3 || coletaPreco.status === 4),
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Finalizar Coleta',
        disabled: coletaPreco === undefined || coletaPreco.status === 2 || coletaPreco.status === 3 || coletaPreco.status === 4,
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Aprovada',
        disabled: coletaPreco === undefined || coletaPreco.status === 0 || coletaPreco.status === 2 || (coletaPrecoFornecedores && !coletaPrecoFornecedores.some(x => x.aprovado === true)),
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 4,
        label: 'Reprovada',
        disabled: coletaPreco === undefined || (coletaPreco.status === 0 || coletaPreco.status === 2),
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Gerou Ordem de Compra',
        disabled: true,
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
    coletaPreco,
    coletaPrecoFornecedores,
  ]);

  /**
   * Funções de carregamento
   */

  const loadFornecedoresDropDown = useCallback(async () => {
    try {
      const fornecedores = await getFornecedoresDetalheDropDownUseCase.getFornecedoresDetalheDropDown();
      setFornecedoresDropdown(fornecedores);
    } catch (e: unknown) {
      setFornecedoresDropdown(undefined);
      throw new Error('Falha ao buscar fornecedores!');
    }
  }, [
    getFornecedoresDetalheDropDownUseCase,
  ]);

  const loadFuncionarioAprovadoresDropDown = useCallback(async () => {
    try {
      const funcionarios = await getFuncionariosAprovadoresDropDownUseCase.getFuncionariosAprovadoresDropDown();
      setFuncionariosAprovacaoDropdown(funcionarios);
    } catch (e: unknown) {
      setFuncionariosAprovacaoDropdown(undefined);
      throw new Error('Falha ao buscar funcionarios aprovadores!');
    }
  }, [
    getFuncionariosAprovadoresDropDownUseCase,
  ]);

  const loadAtributoCompra = useCallback(async () => {
    try {
      const atributoCompra = await getAtributosCompraUseCase.getAtributosCompra(null);
      setAtributoCompra(atributoCompra ?? undefined);
    } catch (e: unknown) {
      setAtributoCompra(undefined);
      throw new Error('Falha ao buscar atributos de compras!');
    }
  }, [
    getAtributosCompraUseCase
  ]);

  const loadColetaPreco = useCallback(async (id?: number) => {
    try {
      const filteredIdColetaPreco = id ?? idColetaPreco;
      const result = await getColetaPrecoByIdUseCase.getColetaPrecoById(filteredIdColetaPreco);

      if (result.idRequisicaoCompra) {
        const produtosServicos = await getProdutosServicosByIdRequisicaoCompraDropDownUseCase
          .getProdutosServicosByIdRequisicaoCompraDropDown(result.idRequisicaoCompra);
        setProdutosServicosDropdown(produtosServicos);
      }

      hookForm.reset();
      hookForm.setValue('nro', result.nrColetaPreco ?? 0);

      if (result.status) {
        hookForm.setValue('status', result.status);
      } else {
        hookForm.resetField('status', { defaultValue: 0 });
      }

      if (result.dataCadastro) {
        hookForm.setValue('dataCadastro', dayjs.utc(result.dataCadastro).local().toDate());
      } else {
        hookForm.setValue('dataCadastro', dayjs.utc(new Date()).local().toDate());
      }

      if (result.idRequisicaoCompra) {
        hookForm.setValue('idRequisicaoCompra', result.idRequisicaoCompra);
      } else {
        hookForm.resetField('idRequisicaoCompra');
      }

      if (result.dataRef) {
        hookForm.setValue('dataColeta', dayjs.utc(result.dataRef).local().toDate());
      } else {
        hookForm.resetField('dataColeta');
      }

      if (result.idCentroCusto) {
        hookForm.setValue('centroCusto', result.idCentroCusto);
      } else {
        hookForm.resetField('centroCusto');
      }

      if (result.idSubconta) {
        hookForm.setValue('idSubconta', result.idSubconta);
      } else {
        hookForm.resetField('idSubconta');
      }

      if (result.idFuncionarioComprador) {
        hookForm.setValue('idFuncionarioComprador', result.idFuncionarioComprador);
      } else {
        hookForm.resetField('idFuncionarioComprador');
      }

      if (result.funcionariosAprovadores) {
        hookForm.setValue('idFuncionarioAprovadores', result.funcionariosAprovadores);
      } else {
        hookForm.resetField('idFuncionarioAprovadores');
      }
      setColetaPreco(result);
    } catch (e: unknown) {
      setColetaPreco(undefined);
      throw new Error('Falha ao buscar coleta de preço!');
    }
  }, [
    idColetaPreco,
    hookForm,
    getColetaPrecoByIdUseCase,
    getProdutosServicosByIdRequisicaoCompraDropDownUseCase,
  ]);

  const loadColetaProdutosServicos = useCallback(async (idColetaPrecoFornecedor: number) => {
    try {
      const coletaProdutosServicos = await getAllColetaProdutoServicoUseCase.getAllColetaProdutoServico(
        idColetaPrecoFornecedor,
      );
      setIdsColetaProdutoServico([]);
      setColetaProdutosServicos(coletaProdutosServicos);
    } catch (e: unknown) {
      setColetaProdutosServicos(undefined);
      throw new Error('Falha ao buscar coleta de preço!');
    }
  }, [
    getAllColetaProdutoServicoUseCase,
  ]);

  const loadColetaPrecoFornecedores = useCallback(async (id?: number, selecionarAprovado = false) => {
    try {
      const filteredIdColetaPreco = id ?? idColetaPreco;

      const coletaPrecoFornecedores = await getAllColetaPrecoFornecedorUseCase.getAllColetaPrecoFornecedor(
        filteredIdColetaPreco,
      );

      if (coletaPrecoFornecedores.length > 0) {
        const aprovadoIdx = selecionarAprovado
          ? coletaPrecoFornecedores.findIndex(x => x.aprovado === true)
          : -1;
        const selectedIdx = aprovadoIdx >= 0
          ? aprovadoIdx + 1
          : Math.min(selectedColetaPrecoFornecedorIdx, coletaPrecoFornecedores.length);
        const coletaPrecoFornecedor = coletaPrecoFornecedores[selectedIdx - 1];

        setSelectedColetaPrecoFornecedorIdx(selectedIdx);
        setSelectedColetaPrecoFornecedor({
          idColetaPrecoFornecedor: coletaPrecoFornecedor.idColetaPrecoFornecedor,
          imposto: coletaPrecoFornecedor.imposto,
          frete: coletaPrecoFornecedor.valorFrete,
          desconto: coletaPrecoFornecedor.desconto,
          acrescimo: coletaPrecoFornecedor.acrescimo,
          dataEntrega: coletaPrecoFornecedor.dataEntrega ? dayjs.utc(coletaPrecoFornecedor.dataEntrega).toDate() : null,
          prazoEntrega: coletaPrecoFornecedor.prazoEntrega,
          condicoesPagamento: coletaPrecoFornecedor.condicoesPagamento,
          observacoes: coletaPrecoFornecedor.observacoes,
        });

        await loadColetaProdutosServicos(coletaPrecoFornecedor.idColetaPrecoFornecedor);
      }

      setColetaPrecoFornecedores(coletaPrecoFornecedores);
    } catch (e: unknown) {
      setColetaPrecoFornecedores(undefined);
      throw new Error('Falha ao buscar coleta de preço!');
    }
  }, [
    idColetaPreco,
    selectedColetaPrecoFornecedorIdx,
    getAllColetaPrecoFornecedorUseCase,
    loadColetaProdutosServicos,
  ]);

  const loadPagamentoColeta = useCallback(async (id?: number) => {
    try {
      const filteredIdColetaPreco = id ?? idColetaPreco;

      const results = await getConfigurarPagamentoColetaPrecoUseCase.getConfigurarPagamentoColetaPreco(
        filteredIdColetaPreco,
      );

      setConfigurarPagamentoColeta(results ?? undefined);
    } catch (e: unknown) {
      setConfigurarPagamentoColeta(undefined);
      throw new Error('Falha ao buscar configuração de pagamento de coleta de preço!');
    }
  }, [
    idColetaPreco,
    getConfigurarPagamentoColetaPrecoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationIdColetaPreco = params.get('id');
      const locationIdRequisicaoCompra = params.get('idRequisicaoCompra');

      const usuarioInfo = await getFuncionarioByIdUsuarioUseCase
        .getFuncionarioByIdUsuario(idUsuario);

      if (usuarioInfo !== null) {
        setUsuarioFuncionarioInfo(usuarioInfo);
        console.log(usuarioInfo);
      }

      await loadAtributoCompra();

      await loadFornecedoresDropDown();

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadFuncionarioAprovadoresDropDown();

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      if (StringUtils.isNumber(locationIdColetaPreco)) {
        const tempIdColetaPreco = Number(locationIdColetaPreco);
        setIdColetaPreco(tempIdColetaPreco);

        await loadRequisicaoCompraDropDown(dt => setRequisicoesComprasDropdown(dt), false);

        await loadColetaPreco(tempIdColetaPreco);

        await loadColetaPrecoFornecedores(tempIdColetaPreco, true);

        await loadPagamentoColeta(tempIdColetaPreco);
      } else {
        await loadRequisicaoCompraDropDown(dt => {
          if (StringUtils.isNumber(locationIdRequisicaoCompra)) {
            const reqCompraDropdown = dt.find(x => x.idRequisicaoCompra === Number(locationIdRequisicaoCompra));
            if (reqCompraDropdown && reqCompraDropdown.idCentroCusto)
              hookForm.setValue('centroCusto', reqCompraDropdown.idCentroCusto);
          }
          setRequisicoesComprasDropdown(dt)
        }, true);

        hookForm.setValue('dataCadastro', dayjs().local().toDate());
        hookForm.setValue('dataColeta', dayjs().local().toDate());

        requestAnimationFrame(() => {
          if (usuarioInfo !== null && usuarioInfo.idFuncionario) {
            hookForm.setValue('idFuncionarioComprador', usuarioInfo.idFuncionario);
          }
        });

        if (StringUtils.isNumber(locationIdRequisicaoCompra)) {
          const tempIdRequisicaoCompra = Number(locationIdRequisicaoCompra);
          hookForm.setValue('idRequisicaoCompra', tempIdRequisicaoCompra);
        }
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      const usuarioInfo = await getFuncionarioByIdUsuarioUseCase
        .getFuncionarioByIdUsuario(idUsuario);

      if (usuarioInfo !== null) {
        setUsuarioFuncionarioInfo(usuarioInfo);
        console.log(usuarioInfo);
      }

      await loadAtributoCompra();

      await loadFornecedoresDropDown();

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadFuncionarioAprovadoresDropDown();

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      if (idColetaPreco) {
        await loadRequisicaoCompraDropDown(dt => setRequisicoesComprasDropdown(dt), false);

        await loadColetaPreco(idColetaPreco);

        await loadColetaPrecoFornecedores(idColetaPreco, true);

        await loadPagamentoColeta(idColetaPreco);
      } else {
        const params = new URLSearchParams(location.search);
        const locationIdRequisicaoCompra = params.get('idRequisicaoCompra');

        await loadRequisicaoCompraDropDown(dt => {
          if (StringUtils.isNumber(locationIdRequisicaoCompra)) {
            const reqCompraDropdown = dt.find(x => x.idRequisicaoCompra === Number(locationIdRequisicaoCompra));
            if (reqCompraDropdown && reqCompraDropdown.idCentroCusto)
              hookForm.setValue('centroCusto', reqCompraDropdown.idCentroCusto);
          }
          setRequisicoesComprasDropdown(dt)
        }, true);

        const usuarioInfo = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);

        hookForm.setValue('dataCadastro', dayjs().startOf('day').toDate());
        hookForm.setValue('dataColeta', dayjs().startOf('day').toDate());

        requestAnimationFrame(() => {
          if (usuarioInfo !== null && usuarioInfo.idFuncionario) {
            hookForm.setValue('idFuncionarioComprador', usuarioInfo.idFuncionario);
            // TODO: Implementar no novo padrao
            // hookForm.setValue('idFuncionarioAprovado', usuarioInfo.idFuncionario);
          }
        });

        if (StringUtils.isNumber(locationIdRequisicaoCompra)) {
          const tempIdRequisicaoCompra = Number(locationIdRequisicaoCompra);
          hookForm.setValue('idRequisicaoCompra', tempIdRequisicaoCompra);
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    location,
    idUsuario,
    idColetaPreco,
    hookForm,
    getFuncionarioByIdUsuarioUseCase,
    loadFornecedoresDropDown,
    loadAtributoCompra,
    loadRequisicaoCompraDropDown,
    loadFuncionarioAprovadoresDropDown,
    loadFuncionariosDropDown,
    loadCentroCustoDropDown,
    loadSubContasDropDown,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    loadPagamentoColeta,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/ColetaPrecos_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleSubmit = useCallback(async (form: ColetaPrecoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (idColetaPreco) {
        await updateColetaPrecoUseCase.updateColetaPreco(
          idColetaPreco,
          form.status,
          form.dataCadastro,
          form.idRequisicaoCompra,
          form.dataColeta,
          form.idSubconta,
          form.idFuncionarioComprador,
          form.idFuncionarioAprovadores,
          form.arquivo1,
          form.limparArquivo1,
          form.arquivo2,
          form.limparArquivo2,
          form.arquivo3,
          form.limparArquivo3,
        );

        if (selectedColetaPrecoFornecedor && coletaProdutosServicos && coletaProdutosServicos.length > 0) {
          const updatedColetaProdutosServicos = coletaProdutosServicos
            .filter(x => idsColetaProdutoServico.some(y => y === x.idColetaProdutoServico));

          await updateColetaPrecoFornecedorUseCase.updateColetaPrecoFornecedor(
            selectedColetaPrecoFornecedor.idColetaPrecoFornecedor,
            selectedColetaPrecoFornecedor.imposto,
            selectedColetaPrecoFornecedor.frete,
            selectedColetaPrecoFornecedor.desconto,
            selectedColetaPrecoFornecedor.acrescimo,
            selectedColetaPrecoFornecedor.dataEntrega,
            selectedColetaPrecoFornecedor.condicoesPagamento,
            selectedColetaPrecoFornecedor.observacoes,
            updatedColetaProdutosServicos,
          );
        }

        await loadColetaPreco();
        await loadColetaPrecoFornecedores();
      } else {
        const createIdColetaPreco = await createColetaPrecoUseCase.createColetaPreco(
          form.status,
          form.dataCadastro,
          form.idRequisicaoCompra,
          form.dataColeta,
          form.idSubconta,
          form.idFuncionarioComprador,
          form.idFuncionarioAprovadores,
          form.arquivo1,
          form.arquivo2,
          form.arquivo3,
        );

        const params = new URLSearchParams(window.location.search);
        params.set("id", createIdColetaPreco.toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);

        await loadColetaPreco(createIdColetaPreco);
        setIdColetaPreco(createIdColetaPreco);
      }
      showEmailAprovacaoModalRef.current = false;
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      await loadColetaPreco();
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idColetaPreco,
    selectedColetaPrecoFornecedor,
    coletaProdutosServicos,
    idsColetaProdutoServico,
    createColetaPrecoUseCase,
    updateColetaPrecoUseCase,
    updateColetaPrecoFornecedorUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleNewFornecedor = useCallback(async (callback: (idFornecedor: number) => void) => {
    startFornecedorDrawer(
      async (form) => {
        try {
          setSaving(true);

          const qualificacoes = await getAllFornecedorQualificacaoUseCase
            .getAllFornecedorQualificacao(null);

          if (qualificacoes.length > 0) {
            const defaultQualificacao = [{ idQualificacao: qualificacoes[0].idQualificacao, qualificado: true, observacao: '' }];

            callback(await createFornecedorUseCase.createFornecedorSimplificado(
              form.ativo,
              form.avaliarFornecedor,
              form.fornecedorProdutos,
              form.fornecedorServicos,
              form.nomeFantasia,
              form.razaoSocial,
              form.centroCusto,
              form.cnpj,
              form.cpf,
              form.observacoes,
              defaultQualificacao,
            ));

            await loadFornecedoresDropDown();

            pushInfoNotification('Sucesso', 'Fornecedor criado com sucesso!');
          }
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao criar fornecedor!'));
        } finally {
          setSaving(false);
        }
      },
    );
  }, [
    getAllFornecedorQualificacaoUseCase,
    createFornecedorUseCase,
    loadFornecedoresDropDown,
    startFornecedorDrawer,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleFinalizarRequisicao = useCallback(async () => {
    if (coletaPreco && requisicoesComprasDropdown) {
      if (window.confirm('Você deseja finalizar a requisição?')) {
        try {
          setSaving(true);
          setPageSaving(true);

          const requisicaoCompra = requisicoesComprasDropdown.find(x => x.idRequisicaoCompra === coletaPreco.idRequisicaoCompra);
          if (requisicaoCompra && requisicaoCompra.idRequisicaoCompra) {
            await finalizarRequisicaoCompraUseCase
              .finalizarRequisicaoCompra(requisicaoCompra.idRequisicaoCompra);

            await loadColetaPreco();

            pushInfoNotification('Sucesso', 'Requisição de compra finalizada com sucesso!');
          }
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    }
  }, [
    coletaPreco,
    requisicoesComprasDropdown,
    finalizarRequisicaoCompraUseCase,
    loadColetaPreco,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleIrParaOrdemCompra = useCallback((openNewTab: boolean) => {
    if (coletaPreco && coletaPreco.idOrdemCompra) {
      if (openNewTab) {
        window.open(`/OrdemCompras_cadastro?id=${coletaPreco.idOrdemCompra}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/OrdemCompras_cadastro?id=${coletaPreco.idOrdemCompra}`);
      }
    }
  }, [
    coletaPreco,
    backOrNavigate,
  ]);

  const handleColetaPrecoFornecedorSubmit = useCallback(async (form: ColetaPrecoFornecedorForm) => {
    if (coletaPreco && coletaPreco.idRequisicaoCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await createColetaPrecoFornecedorUseCase.createColetaPrecoFornecedor(
          coletaPreco.idColetaPreco,
          coletaPreco.idRequisicaoCompra,
          form.fornecedor,
          form.produtosServicos ?? [],
        );

        await loadColetaPrecoFornecedores();
        setColetaPrecoFornecedorModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar requisição de compra!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    coletaPreco,
    createColetaPrecoFornecedorUseCase,
    loadColetaPrecoFornecedores,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleStatusChangeSubmit = useCallback(async (newValue: number | null, prevValue: number | null) => {
    if (idColetaPreco && newValue !== null && newValue !== prevValue) {
      hookForm.handleSubmit(handleSubmit)();
    }
  }, [
    idColetaPreco,
    hookForm,
    handleSubmit,
  ]);

  const handleCopyColetaPrecoFornecedor = useCallback((openState: boolean) => {
    if (selectedColetaPrecoFornecedor) {
      setColetaPrecoFornecedorCopyModal(openState);
    }
  }, [
    selectedColetaPrecoFornecedor,
  ]);

  const handleCopyColetaPrecoFornecedorSubmit = useCallback(async (idFornecedor: number) => {
    if (selectedColetaPrecoFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);

        await copyColetaPrecoFornecedorUseCase.copyColetaPrecoFornecedor(
          selectedColetaPrecoFornecedor.idColetaPrecoFornecedor,
          idFornecedor,
        );

        await loadColetaPrecoFornecedores();

        setColetaPrecoFornecedorCopyModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir coleta de preço fornecedor!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedColetaPrecoFornecedor,
    copyColetaPrecoFornecedorUseCase,
    loadColetaPrecoFornecedores,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleDeleteColetaPrecoFornecedor = useCallback((openState: boolean) => {
    if (selectedColetaPrecoFornecedor) {
      setColetaPrecoFornecedorDeleteModal(openState);
    }
  }, [
    selectedColetaPrecoFornecedor,
  ]);

  const handleDeleteColetaPrecoFornecedorSubmit = useCallback(async () => {
    if (selectedColetaPrecoFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteColetaPrecoFornecedorUseCase.deleteColetaPrecoFornecedor(
          selectedColetaPrecoFornecedor.idColetaPrecoFornecedor,
        );

        await loadColetaPrecoFornecedores();

        setColetaPrecoFornecedorDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir coleta de preço fornecedor!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedColetaPrecoFornecedor,
    deleteColetaPrecoFornecedorUseCase,
    loadColetaPrecoFornecedores,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleAprovarColetaPrecoFornecedorSubmit = useCallback(async () => {
    if (selectedColetaPrecoFornecedor) {
      if (window.confirm('Você deseja aprovar esta coleta?')) {
        try {
          setSaving(true);
          setPageSaving(true);

          if (usuarioFuncionarioInfo === undefined || usuarioFuncionarioInfo.idFuncionario === null || usuarioFuncionarioInfo.idFuncionario === 0) {
            throw new UserFriendlyError('Seu usuário não pode aprovar esta coleta, você precisa configurar um funcionário para ele.');
          }

          await aprovarColetaPrecoFornecedorUseCase.aprovarColetaPrecoFornecedor(
            selectedColetaPrecoFornecedor.idColetaPrecoFornecedor,
            usuarioFuncionarioInfo.idFuncionario,
          );

          await loadColetaPreco();
          await loadColetaPrecoFornecedores();

          pushInfoNotification('Sucesso', 'Coleta aprovada com sucesso!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao aprovar de coleta de preço fornecedor!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    }
  }, [
    usuarioFuncionarioInfo,
    selectedColetaPrecoFornecedor,
    aprovarColetaPrecoFornecedorUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleCancelarAprovacaoColetaPrecoFornecedorSubmit = useCallback(async () => {
    if (idColetaPreco) {
      if (window.confirm('Você deseja cancelar a coleta aprovada?')) {
        try {
          setSaving(true);
          setPageSaving(true);

          await cancelarAprovacaoColetaPrecoFornecedorUseCase
            .cancelarAprovacaoColetaPrecoFornecedor(idColetaPreco);

          await loadColetaPreco();
          await loadColetaPrecoFornecedores();

          pushInfoNotification('Sucesso', 'Aprovação de coleta de preço cancelada!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao cancelar aprovação de coleta de preço fornecedor!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    }
  }, [
    idColetaPreco,
    cancelarAprovacaoColetaPrecoFornecedorUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleGerarOrdemCompraSubmit = useCallback(async () => {
    if (idColetaPreco) {
      try {
        setSaving(true);
        setPageSaving(true);

        await gerarOrdemCompraColetaPrecoUseCase
          .gerarOrdemCompraColetaPreco(idColetaPreco);

        await loadColetaPreco();
        await loadColetaPrecoFornecedores();

        pushInfoNotification('Sucesso', 'Ordem de compra gerada com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao gerar ordem de compra!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idColetaPreco,
    gerarOrdemCompraColetaPrecoUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleGenerateReport = useCallback(async () => {
    if (idColetaPreco) {
      try {
        setLoading(true);
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportColetaPrecoUseCase.getReportColetaPreco(requiredMimeType, idColetaPreco),
        });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao gerar ordem de compra!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idColetaPreco,
    getReportColetaPrecoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleConfigurarPagamentoModalOpenState = useCallback((openState: boolean) => {
    setConfigurarPagamentoModal(openState);
  }, [
  ]);

  const handleConfigurarPagamentoSubmit = useCallback(async (form: ConfigurarPagamentoForm) => {
    if (idColetaPreco && total) {
      try {
        setSaving(true);
        setPageSaving(true);

        await configurarPagamentoColetaPrecoUseCase.configurarPagamentoColetaPreco(
          idColetaPreco,
          total,
          form.titulo,
          form.formaPagamento,
          form.notaFiscal,
          form.pagamentoAvista,
          form.dataPagamento,
          form.pagamentoEm,
          form.entrada,
          form.valorEntrada,
          form.dataEntrada,
          form.nroParcelas,
          form.dataPrimeiroPagamento,
          form.nroDiasEntreParcelas,
          form.informacoesGerais,
        );

        await loadColetaPreco();
        await loadColetaPrecoFornecedores();
        await loadPagamentoColeta();

        pushInfoNotification('Sucesso', 'Configuração de pagamento salvo!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao configurar pagamento de coleta de preço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idColetaPreco,
    total,
    configurarPagamentoColetaPrecoUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    loadPagamentoColeta,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleDeleteConfigurarPagamentoSubmit = useCallback(async () => {
    if (idColetaPreco) {
      if (window.confirm('Você deseja excluir este item?')) {
        try {
          setSaving(true);
          setPageSaving(true);

          await deleteConfigurarPagamentoColetaPrecoUseCase.deleteConfigurarPagamentoColetaPreco(
            idColetaPreco,
          );

          await loadColetaPreco();
          await loadColetaPrecoFornecedores();
          await loadPagamentoColeta();

          pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao excluir registro!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    }
  }, [
    idColetaPreco,
    deleteConfigurarPagamentoColetaPrecoUseCase,
    loadColetaPreco,
    loadColetaPrecoFornecedores,
    loadPagamentoColeta,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleDeleteColetaPrecoOpenState = useCallback((openState: boolean) => {
    setColetaPrecoDeleteModal(openState);
  }, [
  ])

  const handleDeleteColetaPrecoSubmit = useCallback(async () => {
    if (idColetaPreco && coletaPreco && coletaPreco.status !== 2) {
      try {
        setSaving(true);
        setPageSaving(true);

        setColetaPrecoDeleteModal(true);

        await deleteColetaPrecoUseCase.deleteColetaPreco(TelasIds.ColetaPrecos_cadastro, idColetaPreco);

        requestAnimationFrame(() => backOrNavigate(-1));

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir registro!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idColetaPreco,
    coletaPreco,
    deleteColetaPrecoUseCase,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
    backOrNavigate,
  ]);

  const handleNovoPopperActions = useCallback(async (opt: number) => {
    switch (opt) {
      case 1: /* Nova coleta por fornecedor */
        if (coletaPreco && coletaPreco.status === 0) {
          setColetaPrecoFornecedorModal(true);
        }
        break;
    }
  }, [
    coletaPreco,
  ]);

  const handlePopperActions = useCallback(async (opt: number) => {
    switch (opt) {
      case 1: /* Nova coleta */

        setLoading(true);

        const newUrl = `${window.location.pathname}`;
        window.history.replaceState({}, "", newUrl);

        setIdColetaPreco(undefined)
        setColetaPreco(undefined);

        setColetaPrecoFornecedores(undefined);
        setColetaProdutosServicos(undefined);
        setSelectedColetaPrecoFornecedor(undefined);
        setIdsColetaProdutoServico([]);
        setAtributoCompra(undefined);

        setConfigurarPagamentoColeta(undefined);

        hookForm.reset({});

        hookForm.setValue('dataCadastro', dayjs().local().toDate());
        hookForm.setValue('dataColeta', dayjs().local().toDate());

        await loadAtributoCompra();

        await loadFornecedoresDropDown();

        await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

        await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

        await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

        await loadRequisicaoCompraDropDown(dt => setRequisicoesComprasDropdown(dt), true);

        const usuarioInfo = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);

        hookForm.setValue('dataCadastro', dayjs().local().toDate());
        hookForm.setValue('dataColeta', dayjs().local().toDate());

        requestAnimationFrame(() => {
          if (usuarioInfo !== null && usuarioInfo.idFuncionario) {
            hookForm.setValue('idFuncionarioComprador', usuarioInfo.idFuncionario);
            // TODO: Implementar no novo padrao
            // hookForm.setValue('idFuncionarioAprovado', usuarioInfo.idFuncionario);
          }

          setLoading(false);
        });

        break;

      case 2: /* Excluir */
        if (idColetaPreco && coletaPreco) {
          if (coletaPreco.status === 2) {
            pushWarnNotification('Alerta', 'Não é permitido excluir uma coleta de preço com ordem de compra gerada!');
            return;
          }
          setColetaPrecoDeleteModal(true);
        }
        break;
    }
  }, [
    idUsuario,
    idColetaPreco,
    coletaPreco,
    hookForm,
    getFuncionarioByIdUsuarioUseCase,
    loadAtributoCompra,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadCentroCustoDropDown,
    loadSubContasDropDown,
    loadRequisicaoCompraDropDown,
    pushWarnNotification,
  ]);

  const handleDeleteFile = useCallback((nroArquivo: number) => {
    switch (nroArquivo) {
      case 1:
        setColetaPreco(prevState => {
          if (prevState === undefined) return prevState;
          return { ...prevState, nomeArquivo1: null, arquivo1: null };
        });
        break;

      case 2:
        setColetaPreco(prevState => {
          if (prevState === undefined) return prevState;
          return { ...prevState, nomeArquivo2: null, arquivo2: null };
        });
        break;

      case 3:
        setColetaPreco(prevState => {
          if (prevState === undefined) return prevState;
          return { ...prevState, nomeArquivo3: null, arquivo3: null };
        });
        break;
    }
  }, [
  ]);

  const handleEditarColetaProdutoServico = useCallback((_: number, openNewTab: boolean) => {
    if (coletaPreco) {
      if (openNewTab) {
        window.open(`/RequisicaoServicosProdutos_cadastro?id=${coletaPreco.idRequisicaoCompra}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/RequisicaoServicosProdutos_cadastro?id=${coletaPreco.idRequisicaoCompra}`);
      }
    }
  }, [
    coletaPreco,
    backOrNavigate,
  ]);

  const handleDeleteColetaProdutoServicoSubmit = useCallback(async (idColetaProdutoServico: number) => {
    if (idColetaPreco && selectedColetaPrecoFornecedor) {
      if (window.confirm('Você deseja excluir este item?')) {
        try {
          setSaving(true);
          setPageSaving(true);

          await deleteColetaProdutoServicoUseCase.deleteColetaProdutoServico(
            selectedColetaPrecoFornecedor.idColetaPrecoFornecedor,
            idColetaProdutoServico,
          );

          await loadColetaPreco();
          await loadColetaProdutosServicos(selectedColetaPrecoFornecedor.idColetaPrecoFornecedor);

          pushInfoNotification('Sucesso', 'Item excluído com sucesso!');

        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao excluir item!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    }
  }, [
    idColetaPreco,
    selectedColetaPrecoFornecedor,
    deleteColetaProdutoServicoUseCase,
    loadColetaPreco,
    loadColetaProdutosServicos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleDownloadFile = useCallback(async (nroArquivo: number, nomeArquivo: string) => {
    if (coletaPreco) {
      try {
        setCustomLoading(true);

        await downloadColetaPrecoArquivoUseCase
          .downloadColetaPrecoArquivo(coletaPreco.idColetaPreco, nroArquivo, nomeArquivo);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    coletaPreco,
    downloadColetaPrecoArquivoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleNroRequisicaoCompraChange = useCallback(async (idRequisicaoCompra: string | number | null) => {
    if (requisicoesComprasDropdown) {
      try {
        setLoading(true);

        if (typeof idRequisicaoCompra === 'number') {
          const requisicaoCompraDropdown = requisicoesComprasDropdown.find(x => x.idRequisicaoCompra === idRequisicaoCompra) ?? null;
          if (requisicaoCompraDropdown !== null && requisicaoCompraDropdown.idCentroCusto) {
            hookForm.setValue('centroCusto', requisicaoCompraDropdown.idCentroCusto);
          }
        }
        else if (typeof idRequisicaoCompra === 'string') {
          if (StringUtils.isNumber(idRequisicaoCompra)) {
            const requisicaoCompraDropdown = requisicoesComprasDropdown.find(x => x.idRequisicaoCompra === Number(idRequisicaoCompra)) ?? null;
            if (requisicaoCompraDropdown !== null && requisicaoCompraDropdown.idCentroCusto) {
              hookForm.setValue('centroCusto', requisicaoCompraDropdown.idCentroCusto);
            }
          } else {
            throw new Error('Id de requisição de compra invalido!');
          }
        }
        else if (idRequisicaoCompra === null) {
          hookForm.resetField('centroCusto');
        }
        else {
          throw new Error('Id de requisição de compra invalido!');
        }
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar requisição de compra!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    hookForm,
    requisicoesComprasDropdown,
    pushErrorNotification,
  ]);

  const handleColetaPrecoFornecedorModalOpenState = useCallback((openState: boolean) => {
    setColetaPrecoFornecedorModal(openState);
  }, [
  ]);

  const handleFamiliaFornecedorChange = useCallback(async (familia: string | number | null) => {
    try {
      setLoading(true);

      if (typeof familia === 'string' || familia === null) {
        if (typeof familia === 'string') {
          const fornecedores = await getFornecedorByFamiliaDropDownUseCase
            .getFornecedorByFamiliaDropDown(familia);
          setFornecedoresDropdown(fornecedores);
        } else {
          await loadFornecedoresDropDown();
        }
      } else {
        throw new Error('Familia invalida!');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar requisição de compra!'));
    } finally {
      setLoading(false);
    }
  }, [
    getFornecedorByFamiliaDropDownUseCase,
    loadFornecedoresDropDown,
    pushErrorNotification,
  ]);

  const handleColetaPrecoFornecedorChange = useCallback(async (tab: number, idColetaPrecoFornecedor: number) => {
    try {
      setLoading(true);

      if (coletaPrecoFornecedores) {
        const coletaPrecoFornecedor = coletaPrecoFornecedores
          .find(x => x.idColetaPrecoFornecedor === idColetaPrecoFornecedor);

        if (coletaPrecoFornecedor) {
          setSelectedColetaPrecoFornecedor({
            idColetaPrecoFornecedor: coletaPrecoFornecedor.idColetaPrecoFornecedor,
            imposto: coletaPrecoFornecedor.imposto,
            frete: coletaPrecoFornecedor.valorFrete,
            desconto: coletaPrecoFornecedor.desconto,
            acrescimo: coletaPrecoFornecedor.acrescimo,
            dataEntrega: coletaPrecoFornecedor.dataEntrega ? dayjs.utc(coletaPrecoFornecedor.dataEntrega).toDate() : null,
            prazoEntrega: coletaPrecoFornecedor.prazoEntrega,
            condicoesPagamento: coletaPrecoFornecedor.condicoesPagamento,
            observacoes: coletaPrecoFornecedor.observacoes,
          });
        } else {
          throw new Error('Falha ao buscar coleta de preço de fornecedor!');
        }
      } else {
        throw new Error('Falha ao buscar coleta de preço de fornecedor!');
      }

      await loadColetaProdutosServicos(idColetaPrecoFornecedor);

      setSelectedColetaPrecoFornecedorIdx(tab);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar requisição de compra!'));
    } finally {
      setLoading(false);
    }
  }, [
    coletaPrecoFornecedores,
    loadColetaProdutosServicos,
    pushErrorNotification,
  ]);

  const handleQuantidadeColetaChange = useCallback((idColetaProdutoServico: number, value: number | null) => {
    try {
      setColetaProdutosServicos(prev => {
        if (prev === undefined) return prev;
        const idx = prev.findIndex(x => x.idColetaProdutoServico === idColetaProdutoServico);
        if (idx >= 0) {
          const { valorUnitario, quantidadeColeta } = prev[idx];

          setIdsColetaProdutoServico(prevIds => {
            const idxIds = prevIds.findIndex(x => x === idColetaProdutoServico);
            if (idxIds < 0) {
              if (quantidadeColeta !== value) {
                return [...prevIds, idColetaProdutoServico];
              }
            }
            return prevIds;
          });

          prev[idx].quantidadeColeta = value;

          if (prev[idx] && prev[idx].quantidadeColeta !== null && valorUnitario !== null) {
            prev[idx].valorTotal = comprasItemTotal(prev[idx].quantidadeColeta!, valorUnitario);
          } else {
            prev[idx].valorTotal = null;
          }
        }
        return [...prev];
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar a quantidade de coleta!'));
    }
  }, [
    pushErrorNotification,
  ]);

  const handleValorUnitarioChange = useCallback((idColetaProdutoServico: number, value: number | null) => {
    try {
      setColetaProdutosServicos(prev => {
        if (prev === undefined) return prev;
        const idx = prev.findIndex(x => x.idColetaProdutoServico === idColetaProdutoServico);
        if (idx >= 0) {
          const { valorUnitario, quantidadeColeta } = prev[idx];

          setIdsColetaProdutoServico(prevIds => {
            const idxIds = prevIds.findIndex(x => x === idColetaProdutoServico);
            if (idxIds < 0) {
              if (valorUnitario !== value) {
                return [...prevIds, idColetaProdutoServico];
              }
            }
            return prevIds;
          });

          prev[idx].valorUnitario = value === null ? null : roundComprasMoney(value);

          if (prev[idx] && prev[idx].valorUnitario !== null && quantidadeColeta !== null) {
            prev[idx].valorTotal = comprasItemTotal(quantidadeColeta, prev[idx].valorUnitario!);
          } else {
            prev[idx].valorTotal = null;
          }
        }
        return [...prev];
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar o valor unitario!'));
    }
  }, [
    pushErrorNotification,
  ]);

  const handleObservacaoChange = useCallback((idColetaProdutoServico: number, value: string | null) => {
    try {
      setColetaProdutosServicos(prev => {
        if (prev === undefined) return prev;
        const idx = prev.findIndex(x => x.idColetaProdutoServico === idColetaProdutoServico);
        if (idx >= 0) {
          const { observacao } = prev[idx];

          setIdsColetaProdutoServico(prevIds => {
            const idxIds = prevIds.findIndex(x => x === idColetaProdutoServico);
            if (idxIds < 0) {
              if (observacao !== value) {
                return [...prevIds, idColetaProdutoServico];
              }
            }
            return prevIds;
          });

          prev[idx].observacao = value;
        }
        return [...prev];
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao atualizar a observação!'));
    }
  }, [
    pushErrorNotification,
  ]);

  const handleImpostoChange = useCallback((value: number | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, imposto: value };
    });
  }, [
  ]);

  const handleFreteChange = useCallback((value: number | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, frete: value };
    });
  }, [
  ]);

  const handleDescontoChange = useCallback((value: number | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, desconto: value };
    });
  }, [
  ]);

  const handleAcrescimoChange = useCallback((value: number | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, acrescimo: value };
    });
  }, [
  ]);

  const handleDataEntregaChange = useCallback((value: Date | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, dataEntrega: value };
    });
  }, [
  ]);

  const handlePrazoEntregaChange = useCallback((value: string | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, prazoEntrega: value };
    });
  }, [
  ]);

  const handleCondicoesPagamentoChange = useCallback((value: string | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, condicoesPagamento: value };
    });
  }, [
  ]);

  const handleObservacoesChange = useCallback((value: string | null) => {
    setSelectedColetaPrecoFornecedor(prev => {
      if (prev === undefined) return prev;
      return { ...prev, observacoes: value };
    });
  }, [
  ]);

  const handleOpenChangeHistory = useCallback((idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idColetaPreco) {
        changeHistory.handleOpenChangeHistory(idColetaPreco, LogTipoTabela.COLETA_PRECO, true);
      }
    }
  }, [
    idColetaPreco,
    changeHistory,
  ]);

  const handleEmailAprovacaoOpenState = useCallback((openState: boolean) => {
    if (openState && coletaPreco && centroCustosDropdown) {
      if (funcionariosAprovacaoDropdown && coletaPreco.funcionariosAprovadores) {
        const subjects = funcionariosAprovacaoDropdown
          .filter(x => coletaPreco.funcionariosAprovadores?.some(y => y === x.idFuncionario))
          .map(x => x.emailFuncionario);
        setEmailFields('send-to', subjects.join(';'));
      } else {
        setEmailFields('send-to', '');
      }
      setEmailFields('subject', 'Solicitar aprovação de coleta de preço: ' + coletaPreco.nrColetaPreco);
      setEmailFields('attachment', '');

      const idColetaPreco = coletaPreco.idColetaPreco.toString();
      const nrColetaPreco = coletaPreco.nrColetaPreco?.toString() ?? '';
      const centroCusto = centroCustosDropdown?.find(x => x.id === coletaPreco.idCentroCusto)?.title ?? '';
      const comprador = funcionariosDropdown?.find(x => x.idFuncionario === coletaPreco.idFuncionarioComprador)?.nome ?? '';

      setEmailFields('body', emailTemplates.coletaPrecoAprovacao
        .replace('{@@ID_COLETA_PRECO@@}', idColetaPreco)
        .replace('{@@NRO_COLETA@@}', nrColetaPreco)
        .replace('{@@CENTRO_CUSTO@@}', centroCusto)
        .replace('{@@COMPRADOR@@}', comprador));
    }
    setEmailOpenState(openState);
  }, [
    coletaPreco,
    centroCustosDropdown,
    funcionariosDropdown,
    funcionariosAprovacaoDropdown,
    setEmailOpenState,
    setEmailFields,
  ]);

  const handleEmailAprovacaoSubmit = useCallback(async (form: EmailForm) => {
    if (coletaPreco && coletaPreco.idColetaPreco) {
      try {
        setLoading(true);
        await sendAprovacaoColetaPrecoUseCase.sendAprovacaoColetaPreco(
          coletaPreco.idColetaPreco,
          form.subject,
          form.body,
        );
        pushInfoNotification("Sucesso", "Email de aprovação enviado com sucesso!");
        setEmailOpenState(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao enviar email de aprovação de coleta de preço!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    coletaPreco,
    sendAprovacaoColetaPrecoUseCase,
    setEmailOpenState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idColetaPreco,
    coletaPreco: coletaPreco ?? null,
    coletaPrecoFornecedores: coletaPrecoFornecedores ?? [],
    coletaProdutosServicos: coletaProdutosServicos ?? [],
    selectedColetaPrecoFornecedor: selectedColetaPrecoFornecedor ?? null,
    atributoCompra: atributoCompra ?? null,
    configurarPagamentoColeta: configurarPagamentoColeta ?? null,
    coletaPrecoFornecedorModal,
    configurarPagamentoModal,
    requisicoesComprasDropdown: requisicoesComprasDropdown ?? [],
    funcionariosCompradorDropdown: funcionariosCompradorDropdown ?? [],
    funcionariosAprovadorDropdown: filteredFuncionariosAprovadorDropdown ?? [],
    centroCustosDropdown: centroCustosDropdown ?? [],
    subcontasDropdown: subcontasDropdown ?? [],
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    produtosServicosDropdown: produtosServicosDropdown ?? [],
    funcionariosAprovacaoDropdown: funcionariosAprovacaoDropdown ?? [],
    coletaPrecoDeleteModal,
    coletaPrecoFornecedorDeleteModal,
    coletaPrecoFornecedorCopyModal,
    selectedColetaPrecoFornecedorIdx,
    reportViewerState,
    fornecedorStateDrawer,
    emailState,
    hookForm,
    changeHistory,
    status,
    coletaPrecoFornecedorAprovada,
    subTotal,
    total,
    buttonOptions,
    statusOptions,
    handleReload,
    handleBack,
    handleNovoPopperActions,
    handlePopperActions,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleNewFornecedor,
    handleFinalizarRequisicao,
    handleIrParaOrdemCompra,
    handleColetaPrecoFornecedorSubmit,
    handleStatusChangeSubmit,
    handleCopyColetaPrecoFornecedor,
    handleCopyColetaPrecoFornecedorSubmit,
    handleDeleteColetaPrecoFornecedor,
    handleDeleteColetaPrecoFornecedorSubmit,
    handleAprovarColetaPrecoFornecedorSubmit,
    handleCancelarAprovacaoColetaPrecoFornecedorSubmit,
    handleGerarOrdemCompraSubmit,
    handleGenerateReport,
    handleConfigurarPagamentoModalOpenState,
    handleConfigurarPagamentoSubmit,
    handleDeleteConfigurarPagamentoSubmit,
    handleDeleteColetaPrecoOpenState,
    handleDeleteColetaPrecoSubmit,
    handleDeleteFile,
    handleEditarColetaProdutoServico,
    handleDeleteColetaProdutoServicoSubmit,
    handleDownloadFile,
    handleNroRequisicaoCompraChange,
    handleColetaPrecoFornecedorModalOpenState,
    handleFamiliaFornecedorChange,
    handleColetaPrecoFornecedorChange,
    handleQuantidadeColetaChange,
    handleValorUnitarioChange,
    handleObservacaoChange,
    handleImpostoChange,
    handleFreteChange,
    handleDescontoChange,
    handleAcrescimoChange,
    handleDataEntregaChange,
    handlePrazoEntregaChange,
    handleCondicoesPagamentoChange,
    handleObservacoesChange,
    handleOpenChangeHistory,
    handleEmailAprovacaoOpenState,
    handleEmailAprovacaoSubmit,
  };
}
